import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import AdminLayout from "@/components/layout/admin-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, TrendingUp, TrendingDown, Users, Building2, FileText, Zap, Minus, Briefcase, ClipboardList, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from "recharts";
import { format, subDays, subMonths, subYears, startOfDay, endOfDay } from "date-fns";

type TimeRange = '7d' | '30d' | '90d' | '1y' | 'all';
type Granularity = 'day' | 'week' | 'month';

interface TimeseriesData {
  date: string;
  copyEngineCount: number;
  vbrCount: number;
  sponsorCount: number;
  cnaCount: number;
}

interface StationUsage {
  stationId: string;
  stationName: string;
  copyEngineCount: number;
  vbrCount: number;
  sponsorsAdded: number;
  cnasCreated: number;
  totalCount: number;
}

interface UserUsage {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  copyEngineCount: number;
  vbrCount: number;
  sponsorsAdded: number;
  cnasCreated: number;
  totalCount: number;
}

interface UsageSummary {
  totalCopyEngine: number;
  totalVbr: number;
  totalSponsors: number;
  totalCnas: number;
  uniqueUsers: number;
  uniqueStations: number;
  previousPeriodCopyEngine: number;
  previousPeriodVbr: number;
  previousPeriodSponsors: number;
  previousPeriodCnas: number;
}


function getDateRange(range: TimeRange): { startDate: Date; endDate: Date } {
  const endDate = endOfDay(new Date());
  let startDate: Date;
  
  switch (range) {
    case '7d':
      startDate = startOfDay(subDays(new Date(), 7));
      break;
    case '30d':
      startDate = startOfDay(subDays(new Date(), 30));
      break;
    case '90d':
      startDate = startOfDay(subDays(new Date(), 90));
      break;
    case '1y':
      startDate = startOfDay(subYears(new Date(), 1));
      break;
    case 'all':
      startDate = new Date(2020, 0, 1);
      break;
    default:
      startDate = startOfDay(subDays(new Date(), 30));
  }
  
  return { startDate, endDate };
}

function getGranularity(range: TimeRange): Granularity {
  switch (range) {
    case '7d':
      return 'day';
    case '30d':
      return 'day';
    case '90d':
      return 'week';
    case '1y':
      return 'month';
    case 'all':
      return 'month';
    default:
      return 'day';
  }
}

function TrendIndicator({ current, previous }: { current: number; previous: number }) {
  if (previous === 0 && current === 0) {
    return <span className="text-gray-500 text-sm flex items-center gap-1"><Minus className="h-3 w-3" /> No change</span>;
  }
  if (previous === 0) {
    return <span className="text-green-600 text-sm flex items-center gap-1"><TrendingUp className="h-3 w-3" /> New</span>;
  }
  
  const change = ((current - previous) / previous) * 100;
  const isPositive = change >= 0;
  
  return (
    <span className={`text-sm flex items-center gap-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
      {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {Math.abs(change).toFixed(1)}%
    </span>
  );
}

type SortDir = "asc" | "desc";

// Clickable, sort-aware table heading. Numbers default to desc on first
// click, text to asc; clicking the active column flips direction.
function SortableHead({
  label, active, dir, onClick, align = "right",
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  align?: "left" | "right";
}) {
  const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TableHead className={align === "right" ? "text-right" : ""}>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-gray-900 ${active ? "text-gray-900 font-semibold" : ""}`}
        data-testid={`sort-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {label}
        <Icon className="h-3.5 w-3.5" />
      </button>
    </TableHead>
  );
}

function sortRows<T>(rows: T[], key: string, dir: SortDir, valueOf: (row: T, key: string) => string | number): T[] {
  const sorted = [...rows].sort((a, b) => {
    const av = valueOf(a, key);
    const bv = valueOf(b, key);
    const cmp = typeof av === "string" || typeof bv === "string"
      ? String(av).localeCompare(String(bv))
      : Number(av ?? 0) - Number(bv ?? 0);
    return dir === "asc" ? cmp : -cmp;
  });
  return sorted;
}

export default function UsageAnalytics() {
  usePageTitle("App Usage Analytics");
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  
  const isAdmin = user?.role === 'admin';
  
  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You must be an admin to view this page.</p>
            <Button className="mt-4" onClick={() => navigate('/dashboard')}>
              Return to Dashboard
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }
  
  const { startDate, endDate } = getDateRange(timeRange);
  const granularity = getGranularity(timeRange);
  
  const { data: timeseries = [], isLoading: timeseriesLoading } = useQuery<TimeseriesData[]>({
    queryKey: ['/api/admin/usage/timeseries', { startDate: startDate.toISOString(), endDate: endDate.toISOString(), granularity }],
    queryFn: async () => {
      return apiRequest(`/api/admin/usage/timeseries?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&granularity=${granularity}`);
    }
  });
  
  const { data: byStation = [], isLoading: stationLoading } = useQuery<StationUsage[]>({
    queryKey: ['/api/admin/usage/by-station', { startDate: startDate.toISOString(), endDate: endDate.toISOString() }],
    queryFn: async () => {
      return apiRequest(`/api/admin/usage/by-station?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`);
    }
  });
  
  const { data: byUser = [], isLoading: userLoading } = useQuery<UserUsage[]>({
    queryKey: ['/api/admin/usage/by-user', { startDate: startDate.toISOString(), endDate: endDate.toISOString() }],
    queryFn: async () => {
      return apiRequest(`/api/admin/usage/by-user?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`);
    }
  });

  // Client-side sorting for the two breakdown tables.
  const [stationSort, setStationSort] = useState<{ key: string; dir: SortDir }>({ key: "totalCount", dir: "desc" });
  const [userSort, setUserSort] = useState<{ key: string; dir: SortDir }>({ key: "totalCount", dir: "desc" });
  const TEXT_KEYS = ["stationName", "name", "email"];
  const toggleSort = (
    current: { key: string; dir: SortDir },
    set: (v: { key: string; dir: SortDir }) => void,
    key: string,
  ) => {
    if (current.key === key) set({ key, dir: current.dir === "desc" ? "asc" : "desc" });
    else set({ key, dir: TEXT_KEYS.includes(key) ? "asc" : "desc" });
  };
  const userDisplayName = (u: UserUsage) =>
    u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.firstName || 'Unknown';
  const sortedStations = useMemo(
    () => sortRows(byStation, stationSort.key, stationSort.dir, (row, key) => (row as any)[key] ?? 0),
    [byStation, stationSort],
  );
  const sortedUsers = useMemo(
    () => sortRows(byUser, userSort.key, userSort.dir, (row, key) =>
      key === "name" ? userDisplayName(row) : ((row as any)[key] ?? 0)),
    [byUser, userSort],
  );

  const { data: summary, isLoading: summaryLoading } = useQuery<UsageSummary>({
    queryKey: ['/api/admin/usage/summary', { startDate: startDate.toISOString(), endDate: endDate.toISOString() }],
    queryFn: async () => {
      return apiRequest(`/api/admin/usage/summary?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`);
    }
  });

  
  const chartData = timeseries.map(item => ({
    ...item,
    date: format(new Date(item.date), granularity === 'day' ? 'MMM d' : granularity === 'week' ? 'MMM d' : 'MMM yyyy'),
    total: item.copyEngineCount + item.vbrCount
  }));

  return (
    <AdminLayout>
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
          data-testid="button-back-dashboard"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="text-page-title">App Usage Analytics</h1>
            <p className="text-gray-600 mt-1">Sponsor Workbench (new experience) usage across your organization</p>
          </div>
          
          <Select value={timeRange} onValueChange={(v) => setTimeRange(v as TimeRange)}>
            <SelectTrigger className="w-[180px]" data-testid="select-time-range">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Copy Engine Requests</p>
                {summaryLoading ? (
                  <Skeleton className="h-8 w-20 mt-1" />
                ) : (
                  <>
                    <p className="text-2xl font-bold" data-testid="text-copy-engine-total">{summary?.totalCopyEngine || 0}</p>
                    <TrendIndicator current={summary?.totalCopyEngine || 0} previous={summary?.previousPeriodCopyEngine || 0} />
                  </>
                )}
              </div>
              <div className="bg-sky-100 p-3 rounded-lg">
                <FileText className="h-6 w-6 text-sky-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">VBR Requests</p>
                {summaryLoading ? (
                  <Skeleton className="h-8 w-20 mt-1" />
                ) : (
                  <>
                    <p className="text-2xl font-bold" data-testid="text-vbr-total">{summary?.totalVbr || 0}</p>
                    <TrendIndicator current={summary?.totalVbr || 0} previous={summary?.previousPeriodVbr || 0} />
                  </>
                )}
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <Zap className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Sponsors Added</p>
                {summaryLoading ? (
                  <Skeleton className="h-8 w-20 mt-1" />
                ) : (
                  <>
                    <p className="text-2xl font-bold" data-testid="text-sponsors-total">{summary?.totalSponsors || 0}</p>
                    <TrendIndicator current={summary?.totalSponsors || 0} previous={summary?.previousPeriodSponsors || 0} />
                  </>
                )}
              </div>
              <div className="bg-rose-100 p-3 rounded-lg">
                <Briefcase className="h-6 w-6 text-rose-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">CNAs Created</p>
                {summaryLoading ? (
                  <Skeleton className="h-8 w-20 mt-1" />
                ) : (
                  <>
                    <p className="text-2xl font-bold" data-testid="text-cnas-total">{summary?.totalCnas || 0}</p>
                    <TrendIndicator current={summary?.totalCnas || 0} previous={summary?.previousPeriodCnas || 0} />
                  </>
                )}
              </div>
              <div className="bg-indigo-100 p-3 rounded-lg">
                <ClipboardList className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Users</p>
                {summaryLoading ? (
                  <Skeleton className="h-8 w-20 mt-1" />
                ) : (
                  <p className="text-2xl font-bold" data-testid="text-active-users">{summary?.uniqueUsers || 0}</p>
                )}
              </div>
              <div className="bg-emerald-100 p-3 rounded-lg">
                <Users className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Stations</p>
                {summaryLoading ? (
                  <Skeleton className="h-8 w-20 mt-1" />
                ) : (
                  <p className="text-2xl font-bold" data-testid="text-active-stations">{summary?.uniqueStations || 0}</p>
                )}
              </div>
              <div className="bg-amber-100 p-3 rounded-lg">
                <Building2 className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Usage Over Time</CardTitle>
          <CardDescription>Workbench copy, VBRs, sponsor additions, and CNAs by {granularity}</CardDescription>
        </CardHeader>
        <CardContent>
          {timeseriesLoading ? (
            <Skeleton className="h-[300px] w-full" />
          ) : chartData.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No usage data available for this time period
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="copyEngineCount" 
                  stroke="#0ea5e9" 
                  strokeWidth={2}
                  name="Copy Engine" 
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="vbrCount"
                  stroke="#a855f7"
                  strokeWidth={2}
                  name="VBR Requests"
                  dot={{ r: 3 }}
                />
                {/* Palette validated for CVD separation alongside the two
                    existing series (see dataviz check in the commit). */}
                <Line
                  type="monotone"
                  dataKey="sponsorCount"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  name="Sponsor Additions"
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="cnaCount"
                  stroke="#047857"
                  strokeWidth={2}
                  name="CNAs"
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
      
      <Tabs defaultValue="stations" className="mb-6">
        <TabsList>
          <TabsTrigger value="stations" data-testid="tab-stations">By Station</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">By User</TabsTrigger>
        </TabsList>
        
        <TabsContent value="stations">
          <Card>
            <CardHeader>
              <CardTitle>Usage by Station</CardTitle>
              <CardDescription>Workbench activity per station; sponsors and CNAs are attributed via each seller's station assignment</CardDescription>
            </CardHeader>
            <CardContent>
              {stationLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : byStation.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No station usage data available</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableHead label="Station" align="left" active={stationSort.key === "stationName"} dir={stationSort.dir} onClick={() => toggleSort(stationSort, setStationSort, "stationName")} />
                        <SortableHead label="Copy Engine" active={stationSort.key === "copyEngineCount"} dir={stationSort.dir} onClick={() => toggleSort(stationSort, setStationSort, "copyEngineCount")} />
                        <SortableHead label="VBR" active={stationSort.key === "vbrCount"} dir={stationSort.dir} onClick={() => toggleSort(stationSort, setStationSort, "vbrCount")} />
                        <SortableHead label="Sponsors" active={stationSort.key === "sponsorsAdded"} dir={stationSort.dir} onClick={() => toggleSort(stationSort, setStationSort, "sponsorsAdded")} />
                        <SortableHead label="CNAs" active={stationSort.key === "cnasCreated"} dir={stationSort.dir} onClick={() => toggleSort(stationSort, setStationSort, "cnasCreated")} />
                        <SortableHead label="Total" active={stationSort.key === "totalCount"} dir={stationSort.dir} onClick={() => toggleSort(stationSort, setStationSort, "totalCount")} />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedStations.map((station) => (
                        <TableRow key={station.stationId} data-testid={`row-station-${station.stationId}`}>
                          <TableCell className="font-medium">{station.stationName}</TableCell>
                          <TableCell className="text-right">{station.copyEngineCount}</TableCell>
                          <TableCell className="text-right">{station.vbrCount}</TableCell>
                          <TableCell className="text-right">{station.sponsorsAdded ?? 0}</TableCell>
                          <TableCell className="text-right">{station.cnasCreated ?? 0}</TableCell>
                          <TableCell className="text-right font-semibold">{station.totalCount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>Usage by User</CardTitle>
              <CardDescription>Workbench activity per user: copy, VBRs, sponsors added, and CNAs created</CardDescription>
            </CardHeader>
            <CardContent>
              {userLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : byUser.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No user usage data available</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableHead label="User" align="left" active={userSort.key === "name"} dir={userSort.dir} onClick={() => toggleSort(userSort, setUserSort, "name")} />
                        <SortableHead label="Email" align="left" active={userSort.key === "email"} dir={userSort.dir} onClick={() => toggleSort(userSort, setUserSort, "email")} />
                        <SortableHead label="Copy Engine" active={userSort.key === "copyEngineCount"} dir={userSort.dir} onClick={() => toggleSort(userSort, setUserSort, "copyEngineCount")} />
                        <SortableHead label="VBR" active={userSort.key === "vbrCount"} dir={userSort.dir} onClick={() => toggleSort(userSort, setUserSort, "vbrCount")} />
                        <SortableHead label="Sponsors" active={userSort.key === "sponsorsAdded"} dir={userSort.dir} onClick={() => toggleSort(userSort, setUserSort, "sponsorsAdded")} />
                        <SortableHead label="CNAs" active={userSort.key === "cnasCreated"} dir={userSort.dir} onClick={() => toggleSort(userSort, setUserSort, "cnasCreated")} />
                        <SortableHead label="Total" active={userSort.key === "totalCount"} dir={userSort.dir} onClick={() => toggleSort(userSort, setUserSort, "totalCount")} />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedUsers.map((user) => (
                        <TableRow key={user.userId} data-testid={`row-user-${user.userId}`}>
                          <TableCell className="font-medium">{userDisplayName(user)}</TableCell>
                          <TableCell className="text-gray-600">{user.email}</TableCell>
                          <TableCell className="text-right">{user.copyEngineCount}</TableCell>
                          <TableCell className="text-right">{user.vbrCount}</TableCell>
                          <TableCell className="text-right">{user.sponsorsAdded ?? 0}</TableCell>
                          <TableCell className="text-right">{user.cnasCreated ?? 0}</TableCell>
                          <TableCell className="text-right font-semibold">{user.totalCount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
